import { Volume2, VolumeX } from "lucide-react";
import MixSlider, { Fader } from "@/components/creator/mix/MixSlider";
import { STEM_COLORS } from "@/components/creator/stems/StemRow";
import { fmtDb, fmtPan } from "@/lib/mixDeskApi";

export const TAKE_COLOR = "from-fuchsia-400 to-rose-500";
const eq = (v) => fmtDb(v, Number.isInteger(v) ? 0 : 1);

function StripButtons({ name, track, onChange }) {
  return (
    <div className="flex gap-1.5">
      <button type="button" data-testid={`mix-mute-${name}`} aria-pressed={track.mute} onClick={() => onChange({ mute: !track.mute })} aria-label={track.mute ? "Unmute" : "Mute"}
        className={`grid h-7 flex-1 place-items-center rounded-lg border text-[11px] transition-colors duration-150 ${track.mute ? "border-rose-300/60 bg-rose-500/25 text-rose-100" : "border-white/10 text-white/60 hover:text-white"}`}>
        {track.mute ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </button>
      <button type="button" data-testid={`mix-solo-${name}`} aria-pressed={track.solo} onClick={() => onChange({ solo: !track.solo })}
        className={`grid h-7 flex-1 place-items-center rounded-lg border font-mono text-[11px] font-bold transition-colors duration-150 ${track.solo ? "border-amber-300/60 bg-amber-400/25 text-amber-100" : "border-white/10 text-white/60 hover:text-white"}`}>S</button>
    </div>
  );
}

/** One console channel: mute / solo, 3-band EQ, reverb, pan and the level fader. */
export default function ChannelStrip({ name, label, track, audible, onChange, color, children }) {
  return (
    <div data-testid={`mix-strip-${name}`} data-audible={audible ? "1" : "0"}
      className={`flex w-[148px] shrink-0 flex-col gap-3 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/90 p-3 transition-opacity duration-200 ${audible ? "" : "opacity-50"}`}>
      <div className="flex items-center gap-2">
        <span className={`h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b ${color || STEM_COLORS[name] || STEM_COLORS.other}`} />
        <p data-testid={`mix-strip-label-${name}`} className="truncate font-display text-[13px] font-semibold">{label}</p>
      </div>
      <StripButtons name={name} track={track} onChange={onChange} />
      {children}
      <div className="space-y-2 rounded-xl border border-white/[0.06] bg-black/25 p-2">
        <MixSlider id={`mix-high-${name}`} label="High" value={track.high} min={-12} max={12} step={0.5} fmt={eq} onChange={(v) => onChange({ high: v })} />
        <MixSlider id={`mix-mid-${name}`} label="Mid" value={track.mid} min={-12} max={12} step={0.5} fmt={eq} onChange={(v) => onChange({ mid: v })} />
        <MixSlider id={`mix-low-${name}`} label="Low" value={track.low} min={-12} max={12} step={0.5} fmt={eq} onChange={(v) => onChange({ low: v })} />
      </div>
      <MixSlider id={`mix-reverb-${name}`} label="Reverb" value={track.reverb} min={0} max={100} step={1} fmt={(v) => `${Math.round(v)}%`} onChange={(v) => onChange({ reverb: v })} accent="accent-violet-300" />
      <MixSlider id={`mix-pan-${name}`} label="Pan" value={track.pan} min={-1} max={1} step={0.05} fmt={fmtPan} onChange={(v) => onChange({ pan: v })} accent="accent-sky-300" />
      <div className="mt-auto pt-1"><Fader id={`mix-level-${name}`} label={`${label} level`} value={track.db} onChange={(v) => onChange({ db: v })} /></div>
      <p data-testid={`mix-level-value-${name}`} className="text-center font-mono text-[11px] text-white/80">{fmtDb(track.db)}</p>
    </div>
  );
}
